import { useRef, useState } from "react";
import { Award, Download, Crown, Instagram, Twitter, Facebook, Linkedin, MessageCircle, Copy, Check } from "lucide-react";

/**
 * Ambassador Certificate — luxury gold-on-black SVG certificate.
 * Downloadable as a PNG. Serial is derived from tier + referral_code.
 */
const TIER_COLORS = {
  bronze:   { accent: "#B87333", label: "BRONZE" },
  silver:   { accent: "#C0C0C0", label: "SILVER" },
  gold:     { accent: "#D4AF37", label: "GOLD" },
  platinum: { accent: "#E5E4E2", label: "PLATINUM" },
  founding: { accent: "#F5D061", label: "FOUNDING" },
};

function tierBadge(tier) {
  if (!tier) return null;
  return TIER_COLORS[tier.key] || TIER_COLORS.bronze;
}

function serialFor(code, tierKey) {
  const prefix = tierKey ? tierKey.slice(0, 2).toUpperCase() : "AM";
  return `BDU-${prefix}-${(code || "0000").slice(-6)}`;
}

export default function AmbassadorCertificate({ name, tier, dollars, code, memberSince }) {
  const svgRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const t = tierBadge(tier);
  const serial = serialFor(code, tier?.key);
  const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // Compose the share caption
  const shareUrl = code ? `${typeof window !== "undefined" ? window.location.origin : "https://blackdollarsunited.org"}/pledge?ref=${code}` : "https://blackdollarsunited.org";
  const caption = tier
    ? `I just became a ${tier.name} for Black Dollars United — helping raise $${Number(dollars || 0).toLocaleString()} toward the Trillion Dollar Spending Power Fund. 🖤💰\n\nJoin the movement — use my code ${code} at ${shareUrl}\n\n#BlackDollarsUnited #PowerOfTheBlackDollar #BlackWealth`
    : `I'm an Ambassador for Black Dollars United — pooling a trillion dollars of spending power into one fund to rebuild our communities. 🖤💰\n\nUse my code ${code} to join the movement: ${shareUrl}\n\n#BlackDollarsUnited #PowerOfTheBlackDollar`;

  const encoded = encodeURIComponent(caption);
  const encUrl = encodeURIComponent(shareUrl);

  const shareLinks = {
    twitter:  `https://twitter.com/intent/tweet?text=${encoded}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encoded}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
    whatsapp: `https://wa.me/?text=${encoded}`,
  };

  const rasterize = () => new Promise((resolve) => {
    const svgEl = svgRef.current;
    if (!svgEl) return resolve(null);
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = 1200 * scale;
      canvas.height = 850 * scale;
      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, 1200, 850);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    };
    img.src = "data:image/svg+xml;base64," + svg64;
  });

  const download = async () => {
    const blob = await rasterize();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `BDU-Ambassador-Certificate-${serial}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(caption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Native share sheet (mobile) — attaches the PNG when supported
  const nativeShare = async () => {
    const blob = await rasterize();
    const file = blob ? new File([blob], `${serial}.png`, { type: "image/png" }) : null;
    const data = { title: "My Black Dollars United Ambassador Certificate", text: caption, url: shareUrl };
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      data.files = [file];
    }
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        copyCaption();
      }
    } catch (_e) { /* user cancelled */ }
  };

  const instagramShare = async () => {
    // Instagram has no web-share API — hand the user the PNG (if unlocked) + caption ready to paste
    if (tier) {
      await download();
    }
    copyCaption();
  };

  if (!tier) {
    return (
      <div data-testid="cert-locked-block">
        <div className="border border-bdu-gold/20 bg-bdu-surface p-8 text-center" data-testid="cert-locked">
          <Award size={40} className="mx-auto text-bdu-cream/30" />
          <h3 className="mt-4 font-serif text-2xl text-bdu-cream">Your Ambassador Certificate is locked.</h3>
          <p className="mt-3 text-bdu-cream/60 text-sm max-w-md mx-auto">
            Refer <span className="text-bdu-gold">$1,000</span> in donations to unlock your Bronze Ambassador Certificate.
            Every tier above earns a new certificate with your name on the movement.
          </p>
        </div>
        {/* Even locked ambassadors can share their invite */}
        <ShareBar
          shareLinks={shareLinks}
          caption={caption}
          copied={copied}
          copyCaption={copyCaption}
          nativeShare={nativeShare}
          instagramShare={instagramShare}
          testIdPrefix="share-pre"
          heading="Start sharing now — recruit your first $1,000 to unlock Bronze."
        />
      </div>
    );
  }

  return (
    <div data-testid="ambassador-certificate">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow" style={{ fontSize: "0.65rem" }}>Your Certificate · {t.label}</div>
          <div className="mt-1 font-serif text-2xl text-bdu-cream">{tier.name}</div>
        </div>
        <button onClick={download} className="btn-gold text-xs" data-testid="cert-download-btn">
          <Download size={14} /> Download PNG
        </button>
      </div>

      {/* Visual certificate */}
      <div className="border border-bdu-gold/25 bg-black overflow-hidden" style={{ boxShadow: "0 0 60px rgba(212,175,55,0.08)" }}>
        <svg ref={svgRef} viewBox="0 0 1200 850" width="100%" style={{ display: "block", background: "#0A0A0A" }}>
          <defs>
            <linearGradient id="goldBar" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={t.accent} stopOpacity="0.05" />
              <stop offset="0.5" stopColor={t.accent} stopOpacity="0.9" />
              <stop offset="1" stopColor={t.accent} stopOpacity="0.05" />
            </linearGradient>
            <radialGradient id="corner" cx="0" cy="0" r="1">
              <stop offset="0" stopColor={t.accent} stopOpacity="0.18" />
              <stop offset="1" stopColor={t.accent} stopOpacity="0" />
            </radialGradient>
            <pattern id="grain" width="4" height="4" patternUnits="userSpaceOnUse">
              <rect width="4" height="4" fill="#0A0A0A" />
              <circle cx="1" cy="1" r="0.4" fill="rgba(255,255,255,0.02)" />
            </pattern>
          </defs>
          <rect width="1200" height="850" fill="url(#grain)" />
          <rect x="0" y="0" width="1200" height="850" fill="url(#corner)" />
          <rect x="1200" y="850" width="1200" height="850" fill="url(#corner)" transform="rotate(180 1200 850)" />
          {/* Outer frame */}
          <rect x="40" y="40" width="1120" height="770" fill="none" stroke={t.accent} strokeOpacity="0.6" strokeWidth="1.5" />
          <rect x="55" y="55" width="1090" height="740" fill="none" stroke={t.accent} strokeOpacity="0.25" strokeWidth="0.5" />
          {/* Header seal */}
          <g transform="translate(600 130)">
            <circle r="46" fill="none" stroke={t.accent} strokeWidth="1.5" />
            <circle r="36" fill="none" stroke={t.accent} strokeOpacity="0.5" strokeWidth="0.5" />
            <text textAnchor="middle" y="12" fontFamily="Georgia, serif" fontSize="34" fontStyle="italic" fill={t.accent}>BDU</text>
          </g>
          {/* Heading */}
          <text x="600" y="215" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="12" letterSpacing="6" fill="rgba(253,251,247,0.55)">BLACK DOLLARS UNITED</text>
          <text x="600" y="238" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="10" letterSpacing="4" fill="rgba(253,251,247,0.35)">TRILLION DOLLAR SPENDING POWER FUND</text>
          {/* Title */}
          <text x="600" y="335" textAnchor="middle" fontFamily="Georgia, serif" fontSize="58" fill="#FDFBF7">Certificate of Ambassadorship</text>
          <line x1="300" y1="365" x2="900" y2="365" stroke="url(#goldBar)" strokeWidth="1.5" />
          {/* Subtitle */}
          <text x="600" y="410" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="12" letterSpacing="4" fill={t.accent}>THIS HONORS</text>
          {/* Name */}
          <text x="600" y="480" textAnchor="middle" fontFamily="Georgia, serif" fontSize="66" fontStyle="italic" fill="#FDFBF7">{name || "An Ambassador"}</text>
          {/* Tier ribbon */}
          <text x="600" y="540" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="14" letterSpacing="10" fill={t.accent}>{tier.name.toUpperCase()}</text>
          {/* Amount */}
          <text x="600" y="600" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="11" letterSpacing="3" fill="rgba(253,251,247,0.55)">FOR HELPING RAISE</text>
          <text x="600" y="650" textAnchor="middle" fontFamily="Georgia, serif" fontSize="52" fill={t.accent}>${Number(dollars || 0).toLocaleString()}</text>
          <text x="600" y="680" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="10" letterSpacing="2" fill="rgba(253,251,247,0.5)">TOWARD THE TRILLION DOLLAR SPENDING POWER FUND</text>
          {/* Footer */}
          <line x1="120" y1="740" x2="1080" y2="740" stroke={t.accent} strokeOpacity="0.25" strokeWidth="0.5" />
          <text x="120" y="765" fontFamily="Arial, Helvetica, sans-serif" fontSize="9" letterSpacing="2" fill="rgba(253,251,247,0.4)">SERIAL</text>
          <text x="120" y="782" fontFamily="Courier New, monospace" fontSize="12" fill="#FDFBF7">{serial}</text>
          <text x="1080" y="765" textAnchor="end" fontFamily="Arial, Helvetica, sans-serif" fontSize="9" letterSpacing="2" fill="rgba(253,251,247,0.4)">ISSUED</text>
          <text x="1080" y="782" textAnchor="end" fontFamily="Arial, Helvetica, sans-serif" fontSize="12" fill="#FDFBF7">{dateStr}</text>
          <text x="600" y="775" textAnchor="middle" fontFamily="Georgia, serif" fontSize="14" fontStyle="italic" fill={t.accent}>The Power of the Black Dollar</text>
          <text x="600" y="795" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="9" letterSpacing="4" fill="rgba(253,251,247,0.35)">GREG L. BRADFORD · FOUNDER, CEO, PRESIDENT</text>
        </svg>
      </div>

      {/* Disclosure — legally-safe framing */}
      <p className="mt-4 text-[11px] leading-relaxed text-bdu-cream/50" data-testid="cert-disclosure">
        <Crown size={11} className="inline mr-1 text-bdu-gold" />
        Black Dollars United is the <span className="text-bdu-cream/70">nonprofit</span> movement.
        The <span className="text-bdu-cream/70">Trillion Dollar Spending Power Fund</span> is a separate entity with its own
        management team and Board of Directors. This certificate is a <em>symbolic recognition</em> of your ambassadorship —
        it is not equity, ownership, a security, or a promise of financial return.
      </p>

      {/* Share bar */}
      <ShareBar
        shareLinks={shareLinks}
        caption={caption}
        copied={copied}
        copyCaption={copyCaption}
        nativeShare={nativeShare}
        instagramShare={instagramShare}
        testIdPrefix="share-cert"
        heading="Share your certificate — recruit more donors to unlock the next tier."
      />
    </div>
  );
}


function ShareBar({ shareLinks, caption, copied, copyCaption, nativeShare, instagramShare, testIdPrefix, heading }) {
  const btn = "flex flex-col items-center justify-center gap-1.5 py-3 px-2 border border-bdu-gold/20 hover:border-bdu-gold hover:bg-bdu-gold/5 transition-colors text-bdu-cream/80 hover:text-bdu-gold";
  return (
    <div className="mt-8 border border-bdu-gold/25 bg-bdu-surface p-6" data-testid={`${testIdPrefix}-bar`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Share the Movement</div>
          <div className="mt-1 text-sm text-bdu-cream/70">{heading}</div>
        </div>
        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare} className="btn-gold text-xs" data-testid={`${testIdPrefix}-native`}>
            Share…
          </button>
        )}
      </div>

      {/* Platform buttons */}
      <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 gap-2">
        {instagramShare && (
          <button onClick={instagramShare} className={btn} data-testid={`${testIdPrefix}-instagram`} title="Downloads PNG + copies caption for Instagram">
            <Instagram size={20} />
            <span className="text-[10px] uppercase tracking-widest">Instagram</span>
          </button>
        )}
        <a href={shareLinks.twitter} target="_blank" rel="noreferrer" className={btn} data-testid={`${testIdPrefix}-twitter`}>
          <Twitter size={20} />
          <span className="text-[10px] uppercase tracking-widest">Twitter / X</span>
        </a>
        <a href={shareLinks.facebook} target="_blank" rel="noreferrer" className={btn} data-testid={`${testIdPrefix}-facebook`}>
          <Facebook size={20} />
          <span className="text-[10px] uppercase tracking-widest">Facebook</span>
        </a>
        <a href={shareLinks.linkedin} target="_blank" rel="noreferrer" className={btn} data-testid={`${testIdPrefix}-linkedin`}>
          <Linkedin size={20} />
          <span className="text-[10px] uppercase tracking-widest">LinkedIn</span>
        </a>
        <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" className={btn} data-testid={`${testIdPrefix}-whatsapp`}>
          <MessageCircle size={20} />
          <span className="text-[10px] uppercase tracking-widest">WhatsApp</span>
        </a>
      </div>

      {/* Caption preview + copy */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-bdu-cream/50">Caption Preview</span>
          <button onClick={copyCaption} className="text-xs text-bdu-gold hover:underline flex items-center gap-1.5" data-testid={`${testIdPrefix}-copy`}>
            {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>
        <pre className="whitespace-pre-wrap text-xs text-bdu-cream/70 leading-relaxed p-4 border border-bdu-gold/15 bg-bdu-black/40 font-sans" data-testid={`${testIdPrefix}-caption`}>
{caption}
        </pre>
      </div>

      <p className="mt-3 text-[10px] text-bdu-cream/40">
        Instagram doesn't allow direct web posting — clicking Instagram will download your certificate PNG and copy the caption so you can paste both into a new post.
      </p>
    </div>
  );
}
